import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight, CreditCard, ShieldCheck, Star, Truck, X } from "lucide-react";
import { useScrollLock } from "@/utils/useScrollLock";
import type { Product } from "@/mocks/products";
import { PINNED_PRODUCT_ID } from "@/utils/collections";
import { instagramProfile, type InstagramPost } from "@/data/instagramPosts";
import type { Testimonial } from "@/data/testimonials";
import LpProductCard from "./LpProductCard";
import { useAutoplay, useCarousel } from "./useCarousel";

const base = import.meta.env.BASE_URL;

export function Hero() {
  return (
    <section className="lp-hero" aria-labelledby="lp-hero-title">
      <div className="lp-hero-bg" aria-hidden="true" />
      <div className="lp-hero-inner">
        <p className="lp-eyebrow">ACESSÓRIOS QUE</p>
        <h1 id="lp-hero-title">COMPLETAM<br />SUA ESSÊNCIA</h1>
        <p className="lp-lead">Beleza, praticidade e estilo em cada detalhe.<br className="lp-br" /> Descubra acessórios que transformam<br className="lp-br" /> o seu dia a dia.</p>
        <Link to="/produtos" className="lp-btn-gold">COMPRAR AGORA <ArrowRight size={16} aria-hidden="true" /></Link>
        <ul className="lp-trust">
          <li><Truck size={22} aria-hidden="true" /><span>Entrega para<br />todo o Brasil</span></li>
          <li><CreditCard size={22} aria-hidden="true" /><span>Pagamento<br />100% seguro</span></li>
          <li><ShieldCheck size={22} aria-hidden="true" /><span>Compra garantida<br />e suporte especializado</span></li>
        </ul>
      </div>
    </section>
  );
}

export interface LpCategory { id: string; label: string; image?: string }

export function CategoryStrip({ categories, loading }: { categories: LpCategory[]; loading: boolean }) {
  if (!loading && categories.length === 0) return null;
  return (
    <nav className="lp-cats" aria-label="Categorias">
      <ul>
        {loading && categories.length === 0
          ? Array.from({ length: 8 }, (_, i) => <li key={i} className="lp-cat skeleton" aria-hidden="true"><span className="lp-cat-circle" /><b /><small /></li>)
          : categories.map((category) => (
              <li key={category.id} className="lp-cat">
                <Link to={`/produtos?categoria=${encodeURIComponent(category.id)}`}>
                  <span className="lp-cat-circle">{category.image && <img src={category.image} alt="" loading="lazy" decoding="async" />}</span>
                  <b>{category.label.split("/").map((part, index, all) => <span key={index}>{part}{index < all.length - 1 && <>/<wbr /></>}</span>)}</b>
                  <small>Ver produtos <ArrowRight size={11} aria-hidden="true" /></small>
                </Link>
              </li>
            ))}
      </ul>
    </nav>
  );
}

interface CarouselProps {
  id: string;
  eyebrow?: string;
  title: ReactNode;
  subtitle?: string;
  linkHref: string;
  linkLabel: string;
  products: Product[];
  loading: boolean;
  onAdd: (product: Product) => void;
  ranked?: boolean;
  tone?: "light" | "plain";
}

export function ProductCarousel({ id, eyebrow, title, subtitle, linkHref, linkLabel, products, loading, onAdd, ranked, tone = "plain" }: CarouselProps) {
  const track = useRef<HTMLDivElement>(null);
  const arrows = useCarousel(track, products.length + (loading ? 1 : 0));
  if (!loading && products.length === 0) return null;
  return (
    <section className={`lp-section ${tone === "light" ? "lp-section-light" : ""}`} aria-labelledby={`${id}-title`}>
      <div className="lp-head">
        <div>
          {eyebrow && <p className="lp-section-eyebrow">{eyebrow}</p>}
          <h2 id={`${id}-title`}>{title}</h2>
          {subtitle && <p className="lp-sub">{subtitle}</p>}
        </div>
        <Link to={linkHref} className="lp-link">{linkLabel} <ArrowRight size={14} aria-hidden="true" /></Link>
      </div>
      <div className="lp-carousel">
        {arrows.overflow && <button type="button" className="lp-arrow prev" aria-label="Anterior" disabled={!arrows.prev} onClick={() => arrows.scrollBy(-1)}><ChevronLeft size={20} /></button>}
        <div className="lp-track" ref={track} tabIndex={0} aria-label={`${typeof title === "string" ? title : "Produtos"} — deslize para ver mais`}>
          {loading && products.length === 0
            ? Array.from({ length: 5 }, (_, i) => <div key={i} className="lp-card skeleton" aria-hidden="true"><div className="lp-card-media" /><div className="lp-card-body"><i /><i /><i /></div></div>)
            : products.map((product, index) => <LpProductCard key={product.id} product={product} onAdd={onAdd} rank={ranked ? index + 1 : undefined} />)}
        </div>
        {arrows.overflow && <button type="button" className="lp-arrow next" aria-label="Próximo" disabled={!arrows.next} onClick={() => arrows.scrollBy(1)}><ChevronRight size={20} /></button>}
      </div>
    </section>
  );
}

export function FeatureBanner() {
  return (
    <section className="lp-feature" aria-labelledby="lp-feature-title">
      <img className="lp-feature-photo" src={`${base}kit-trays.webp`} alt="" loading="lazy" decoding="async" />
      <div className="lp-feature-copy">
        <p className="lp-section-eyebrow">Produto em destaque</p>
        <h2 id="lp-feature-title">KIT EMPREENDEDOR</h2>
        <p className="lp-feature-name">Brincos Sortidos Bijuteria Rica Tanto</p>
        <p className="lp-feature-text">Variedade, qualidade e estilo para revender e aumentar sua renda. Mais de 15 modelos em um kit completo.</p>
        <Link to={`/produto/${PINNED_PRODUCT_ID}`} className="lp-btn-gold">VER PRODUTO <ArrowRight size={16} aria-hidden="true" /></Link>
      </div>
    </section>
  );
}

export function Testimonials({ items }: { items: Testimonial[] }) {
  const track = useRef<HTMLDivElement>(null);
  const arrows = useCarousel(track, items.length);
  useAutoplay(track, items.length > 0);
  if (items.length === 0) return null;
  return (
    <section className="lp-testimonials" aria-labelledby="lp-testimonials-title">
      <h2 id="lp-testimonials-title"><span />O que nossas clientes dizem<span /></h2>
      <p className="lp-testimonials-sub">Centenas de mulheres já confiam na Rica Tanto</p>
      <div className="lp-carousel dark">
        {arrows.overflow && <button type="button" className="lp-arrow prev" aria-label="Depoimento anterior" disabled={!arrows.prev} onClick={() => arrows.scrollBy(-1)}><ChevronLeft size={20} /></button>}
        <div className="lp-track" ref={track}>
          {items.map((item) => (
            <figure key={item.name} className="lp-quote">
              <div className="lp-stars" role="img" aria-label={`${item.stars} de 5 estrelas`}>{Array.from({ length: item.stars }, (_, i) => <Star key={i} size={15} fill="currentColor" aria-hidden="true" />)}</div>
              <blockquote>“{item.text}”</blockquote>
              <figcaption>{item.name}<em> · {item.city}</em></figcaption>
            </figure>
          ))}
        </div>
        {arrows.overflow && <button type="button" className="lp-arrow next" aria-label="Próximo depoimento" disabled={!arrows.next} onClick={() => arrows.scrollBy(1)}><ChevronRight size={20} /></button>}
      </div>
    </section>
  );
}

// Código do post/reel na URL (instagram.com/<perfil>/reel/CODIGO/ ou /p/CODIGO/).
function instagramCode(url: string): string | null {
  return url.match(/instagram\.com\/(?:[^/]+\/)?(?:reel|reels|p|tv)\/([\w-]+)/)?.[1] ?? null;
}

// Player do Instagram dentro do site (embed oficial), numa janela por cima da página.
function InstagramPlayer({ post, onClose }: { post: InstagramPost; onClose: () => void }) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useScrollLock(true);
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const code = instagramCode(post.url);
  return (
    <div className="lp-player" role="dialog" aria-modal="true" aria-label={post.alt} onClick={onClose}>
      <div className="lp-player-box" onClick={(event) => event.stopPropagation()}>
        <button ref={closeRef} type="button" className="lp-player-close" aria-label="Fechar vídeo" onClick={onClose}><X size={20} /></button>
        {code && (
          <iframe
            src={`https://www.instagram.com/reel/${code}/embed/`}
            title={post.alt}
            allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
          />
        )}
        <a href={post.url} target="_blank" rel="noopener noreferrer" className="lp-player-link">Abrir no Instagram</a>
      </div>
    </div>
  );
}

export function InstagramGrid({ posts, sample }: { posts: InstagramPost[]; sample: boolean }) {
  const [playing, setPlaying] = useState<InstagramPost | null>(null);
  const close = useCallback(() => setPlaying(null), []);
  if (posts.length === 0) return null;
  return (
    <section className="lp-section" aria-labelledby="lp-insta-title">
      <div className="lp-head">
        <div>
          <h2 id="lp-insta-title">Acompanhe nosso Instagram</h2>
          <p className="lp-sub">Novidades, inspirações e muito mais{sample ? " (visualização: fotos de exemplo)" : ""}</p>
        </div>
        <a href={instagramProfile.url} target="_blank" rel="noopener noreferrer" className="lp-link"><i className="ri-instagram-line" aria-hidden="true"></i> {instagramProfile.handle}</a>
      </div>
      <ul className="lp-insta" data-count={Math.min(posts.length, 6)} style={{ "--cols": Math.min(posts.length, 6) } as CSSProperties}>
        {posts.slice(0, 6).map((post, i) => (
          <li key={post.url + i}>
            {instagramCode(post.url) ? (
              <button type="button" onClick={() => setPlaying(post)} aria-label={`Assistir: ${post.alt}`}>
                <img src={post.image} alt="" loading="lazy" decoding="async" />
                <span className="lp-insta-play" aria-hidden="true"><i className="ri-play-fill"></i></span>
              </button>
            ) : (
              <a href={post.url} target="_blank" rel="noopener noreferrer" aria-label={`Ver no Instagram: ${post.alt}`}>
                <img src={post.image} alt={post.alt} loading="lazy" decoding="async" />
                <i className="ri-instagram-line" aria-hidden="true"></i>
              </a>
            )}
          </li>
        ))}
      </ul>
      {playing && <InstagramPlayer post={playing} onClose={close} />}
    </section>
  );
}
